// @systeme: interface
// La feuille de capture : un bouton, un vocal. Ou un texte. Rien d'autre n'est demandé
// à ce moment-là. Le vocal part tout de suite, ou dès que le réseau revient.
import { useEffect, useRef, useState } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Camera, Mic, Square } from 'lucide-react';
import type { ModeCapture } from '@foyer/shared';
import { Enregistreur, enregistrementDisponible } from '../lib/enregistreur';
import { fileAttente, nouvelleCle } from '../lib/file-attente';
import { nomReduit, reduireImage } from '../lib/images';
import { couleurs, tailles } from '../theme/theme';
import { fonctionActive } from '../lib/fonctionnalites';
import { useSessionConnectee } from '../session/SessionContext';
import { useMessages } from './Messages';

interface Props {
  ouvert: boolean;
  mode: ModeCapture;
  onFermer(): void;
  /** Rappelé quand une capture est partie (ou gardée pour plus tard). */
  onCapture(): void;
}

const DUREE_MAX_SECONDES = { normal: 180, vide_ma_tete: 600 } as const;

export function FeuilleCapture({ ouvert, mode, onFermer, onCapture }: Props) {
  const session = useSessionConnectee();
  const { dire } = useMessages();
  const [etat, setEtat] = useState<'choix' | 'enregistre' | 'envoi'>('choix');
  const [secondes, setSecondes] = useState(0);
  const [texte, setTexte] = useState('');
  const [photo, setPhoto] = useState<File | undefined>();
  const [photoSante, setPhotoSante] = useState(false);
  const enregistreur = useRef<Enregistreur | undefined>(undefined);
  const entreePhoto = useRef<HTMLInputElement | null>(null);
  const micro = enregistrementDisponible();

  useEffect(() => {
    if (etat !== 'enregistre') return;
    const minuterie = setInterval(() => setSecondes((s) => s + 1), 1000);
    return () => clearInterval(minuterie);
  }, [etat]);

  useEffect(() => {
    if (etat === 'enregistre' && secondes >= DUREE_MAX_SECONDES[mode]) void terminer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondes]);

  async function commencer() {
    try {
      enregistreur.current = new Enregistreur();
      await enregistreur.current.demarrer();
      setSecondes(0);
      setEtat('enregistre');
    } catch {
      dire('Le téléphone n’a pas donné accès au micro. Tu peux écrire à la place.');
    }
  }

  async function terminer() {
    if (!enregistreur.current) return;
    setEtat('envoi');
    const audio = await enregistreur.current.arreter();
    enregistreur.current = undefined;
    if (audio.size < 500) {
      dire('Je n’ai rien entendu. Réessaie en parlant un peu plus près.');
      setEtat('choix');
      return;
    }
    await envoyer({ type: 'audio', audio });
  }

  async function envoyerTexte() {
    if (texte.trim().length === 0) return;
    setEtat('envoi');
    await envoyer({ type: 'texte', texte: texte.trim() });
    setTexte('');
  }

  async function envoyerPhoto() {
    if (!photo) return;
    setEtat('envoi');
    // Réduite avant de partir : moins lourde pour le réseau, moins chère à lire.
    const reduite = await reduireImage(photo);
    await envoyer({
      type: 'photo',
      photo: reduite,
      sante: photoSante,
      nom: nomReduit(photo.name, reduite !== photo),
    });
    setPhoto(undefined);
    setPhotoSante(false);
  }

  async function envoyer(
    capture:
      | { type: 'audio'; audio: Blob }
      | { type: 'texte'; texte: string }
      | { type: 'photo'; photo: Blob; sante: boolean; nom: string },
  ) {
    const parti = await fileAttente().ajouter({ cle: nouvelleCle(), mode, ...capture });
    if (parti) {
      dire(
        mode === 'vide_ma_tete'
          ? 'C’est noté. Je te montre demain matin.'
          : 'C’est noté. Je te prépare les fiches.',
      );
    } else {
      dire(
        capture.type === 'audio'
          ? 'Ton vocal n’est pas parti, je réessaie dès que le réseau revient.'
          : capture.type === 'photo'
            ? 'Ta photo n’est pas partie, je réessaie dès que le réseau revient.'
            : 'Ton message n’est pas parti, je réessaie dès que le réseau revient.',
      );
    }
    setEtat('choix');
    onCapture();
    onFermer();
  }

  function annuler() {
    enregistreur.current?.annuler();
    enregistreur.current = undefined;
    setEtat('choix');
    onFermer();
  }

  const minutes = Math.floor(secondes / 60);
  const reste = String(secondes % 60).padStart(2, '0');

  return (
    <Drawer anchor="bottom" open={ouvert} onClose={etat === 'enregistre' ? undefined : annuler}>
      <Box sx={{ px: `${tailles.marge}px`, pt: 2, pb: 'calc(24px + env(safe-area-inset-bottom))' }}>
        <Typography variant="h2" component="h2" sx={{ mb: 0.5 }}>
          {mode === 'vide_ma_tete' ? 'Vide ta tête' : 'Qu’est-ce que tu as en tête ?'}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {mode === 'vide_ma_tete'
            ? 'Parle sans structure, aussi longtemps que tu veux. Je range en silence, je te montre demain matin.'
            : 'Dis tout d’un coup : le dentiste, l’anniversaire, les courses. Je découpe.'}
        </Typography>

        {etat === 'enregistre' ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
            <Typography variant="h1" component="p" aria-live="polite">
              {minutes}:{reste}
            </Typography>
            <IconButton
              aria-label="Terminer l’enregistrement"
              onClick={() => void terminer()}
              sx={{
                width: 88,
                height: 88,
                bgcolor: couleurs.terreCuite,
                color: couleurs.blanc,
                '&:hover': { bgcolor: couleurs.terreCuite },
              }}
            >
              <Square size={36} />
            </IconButton>
            <Typography color="text.secondary">Je t’écoute. Appuie pour terminer.</Typography>
            <Button color="inherit" onClick={annuler}>
              Annuler
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {micro && (
              <Stack alignItems="center" spacing={1} sx={{ py: 1 }}>
                <IconButton
                  aria-label="Parler"
                  disabled={etat === 'envoi'}
                  onClick={() => void commencer()}
                  sx={{
                    width: 88,
                    height: 88,
                    bgcolor: couleurs.terreCuite,
                    color: couleurs.blanc,
                    '&:hover': { bgcolor: couleurs.terreCuite },
                  }}
                >
                  <Mic size={40} />
                </IconButton>
                <Typography color="text.secondary">Appuie et parle</Typography>
              </Stack>
            )}
            {mode === 'normal' && fonctionActive(session, 'photos') && (
              <Box>
                <input
                  ref={entreePhoto}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  aria-label="Choisir une photo"
                  onChange={(e) => setPhoto(e.target.files?.[0])}
                />
                {photo ? (
                  <Stack spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Photo prête : {photo.name}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={photoSante}
                          onChange={(e) => setPhotoSante(e.target.checked)}
                        />
                      }
                      label="C’est un document de santé (ordonnance, carnet, résultat)"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {photoSante
                        ? 'Elle sera gardée avec ta note, sans jamais être lue par l’assistant.'
                        : 'L’assistant la lit pour en tirer les choses à faire, comme un vocal.'}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        disabled={etat === 'envoi'}
                        onClick={() => void envoyerPhoto()}
                      >
                        Envoyer la photo
                      </Button>
                      <Button color="inherit" onClick={() => setPhoto(undefined)}>
                        Annuler
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Button
                    startIcon={<Camera size={20} />}
                    color="inherit"
                    onClick={() => entreePhoto.current?.click()}
                    sx={{ px: 0, mb: 0.5 }}
                  >
                    Photographier un document
                  </Button>
                )}
              </Box>
            )}
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                void envoyerTexte();
              }}
            >
              <TextField
                value={texte}
                onChange={(e) => setTexte(e.target.value)}
                placeholder={micro ? 'Ou écris ici…' : 'Écris ce que tu as en tête…'}
                multiline
                minRows={2}
                inputProps={{ maxLength: 5000, 'aria-label': 'Message' }}
                autoFocus={!micro}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={etat === 'envoi' || texte.trim().length === 0}
                sx={{ mt: 1.5 }}
              >
                Envoyer
              </Button>
            </Box>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
